import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import TableSearchInput from '../components/TableSearchInput';
import ColumnFilterInput from '../components/ColumnFilterInput';
import SearchableSelectWithAdd from '../components/SearchableSelectWithAdd';
import { matchesOrderLine, normalizeSearch } from '../utils/textSearch';
import { quoteChargesAdjustment } from '../utils/quoteLineMath';
import { formatInr } from '../utils/formatInr';

const emptyOrderLineFilters = () => ({ item: '', section: '', sectionLabel: '' });

const fmt = (d) => d ? new Date(d).toLocaleString('en-IN',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:false}) : '—';
const fmtDateOnly = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'2-digit',year:'numeric'}) : 'Not set';
const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
const UOM_OPTS = ['sheet', 'set', 'sqft', 'rft', 'unit', 'mm', 'cm', 'ft', 'kg', 'litre', 'nos'];
const DRAG_MIME_ORDER = 'application/x-tatsavi-order-row';
const MODE_OF_PAYMENT_OPTIONS = ['Cash', 'Online UPI'];

const orderFileName = (file) => {
  if (!file) return 'Attachment';
  if (typeof file === 'string') return String(file).split('/').pop() || 'Attachment';
  return file.name || file.filename || file.title || 'Attachment';
};

const orderFileUrl = (file) => {
  if (!file) return '';
  if (typeof file === 'string') {
    return file.startsWith('http') ? file : `${BASE_URL}${file}`;
  }
  return file.url || '';
};

const isOrderImage = (file) => {
  const type = typeof file === 'string' ? '' : String(file?.type || file?.mimetype || '');
  const name = orderFileName(file).toLowerCase();
  return type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
};

const isOrderPdf = (file) => {
  const type = typeof file === 'string' ? '' : String(file?.type || file?.mimetype || '');
  return type === 'application/pdf' || orderFileName(file).toLowerCase().endsWith('.pdf');
};

const orderLineName = (line) =>
  line?.itemName ||
  line?.materialMaster?.name ||
  line?.item?.name ||
  line?.name ||
  '—';

const orderLineDescription = (line) =>
  line?.lineDescription ||
  line?.materialMaster?.description ||
  line?.item?.description ||
  '';

const visibleWorkOrderCharges = (order) =>
  (Array.isArray(order?.charges) ? order.charges : []).filter(
    (c) => String(c?.chargeName || c?.labourType || '').trim() || (Number(c?.value) || 0) !== 0
  );

const orderHighlightItems = (order) => {
  if (order?.orderType === 'Work Order') {
    return visibleWorkOrderCharges(order)
      .map((c) => String(c?.chargeName || c?.labourType || '').trim())
      .filter(Boolean);
  }
  return (Array.isArray(order?.items) ? order.items : [])
    .map((item) => orderLineName(item))
    .map((name) => String(name || '').trim())
    .filter((name) => name && name !== '—' && name !== 'â€”');
};

/** `short` = compact row in the picker (same pattern as vendor names); `label` = full text for search match */
const ORDER_TYPE_OPTIONS = [
  { value: 'Procurement', short: 'Procurement', label: 'Procurement (materials / hardware)' },
  { value: 'Work Order', short: 'Work order', label: 'Work order (labour charges → Financials)' },
];

// ── Add Items: pick from Material Master (hardware / purchase) ─────────────────
function SelectItemsModal({ onClose, onAdd }) {
  const [all, setAll] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [flName, setFlName] = useState('');
  const [flDesc, setFlDesc] = useState('');
  const [flUnit, setFlUnit] = useState('');

  useEffect(() => {
    setLoading(true);
    api
      .get('/material-master', { params: { limit: 500, page: 1 } })
      .then((r) => setAll(r.data?.data?.materials || []))
      .catch(() => setAll([]))
      .finally(() => setLoading(false));
  }, []);

  const nn = normalizeSearch(flName);
  const nd = normalizeSearch(flDesc);
  const nu = normalizeSearch(flUnit);
  const filtered = all.filter((m) => {
    if (nn && !String(m.name ?? '').toLowerCase().includes(nn)) return false;
    if (nd && !String(m.description ?? '').toLowerCase().includes(nd)) return false;
    if (nu && !String(m.unit ?? '').toLowerCase().includes(nu)) return false;
    return true;
  });

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const submit = () => {
    onAdd(all.filter((m) => selected[m._id]));
    onClose();
  };

  const thS = { padding: '6px 8px', fontSize: 10, fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #E5E7EB', textAlign: 'left', background: '#F9FAFB' };
  const tdS = { padding: '8px', borderBottom: '1px solid #F3F4F6', fontSize: 12, verticalAlign: 'top' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '1px solid #E5E7EB' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Add items — Material Master</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#9CA3AF' }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#9CA3AF' }}>Loading…</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thS, width: 36 }} />
                  <th style={thS}>Name</th>
                  <th style={thS}>Description</th>
                  <th style={{ ...thS, width: 80 }}>Unit</th>
                </tr>
                <tr style={{ background: '#FAFAFA' }}>
                  <th style={{ ...thS, paddingTop: 4, paddingBottom: 8 }} />
                  <th style={{ ...thS, paddingTop: 4, paddingBottom: 8, verticalAlign: 'top' }}>
                    <ColumnFilterInput value={flName} onChange={(e) => setFlName(e.target.value)} placeholder="Filter name…" aria-label="Filter by name" />
                  </th>
                  <th style={{ ...thS, paddingTop: 4, paddingBottom: 8, verticalAlign: 'top' }}>
                    <ColumnFilterInput value={flDesc} onChange={(e) => setFlDesc(e.target.value)} placeholder="Filter description…" aria-label="Filter by description" />
                  </th>
                  <th style={{ ...thS, paddingTop: 4, paddingBottom: 8, verticalAlign: 'top' }}>
                    <ColumnFilterInput value={flUnit} onChange={(e) => setFlUnit(e.target.value)} placeholder="Filter unit…" aria-label="Filter by unit" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr
                    key={m._id}
                    onClick={() => toggle(m._id)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#F9FAFB'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={tdS}>
                      <input type="checkbox" checked={!!selected[m._id]} onChange={() => toggle(m._id)} onClick={(e) => e.stopPropagation()} />
                    </td>
                    <td style={{ ...tdS, fontWeight: 600, color: '#1F2937' }}>{m.name}</td>
                    <td style={{ ...tdS, color: '#6B7280', fontSize: 11 }}>{m.description || '—'}</td>
                    <td style={tdS}><span style={{ background: '#F3F4F6', padding: '2px 8px', borderRadius: 20, fontSize: 11 }}>{m.unit || '—'}</span></td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#9CA3AF', fontSize: 12 }}>No materials — add them under Masters → Material Master</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ padding: '10px 18px', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#9CA3AF' }}>{Object.values(selected).filter(Boolean).length} selected</span>
          <button type="button" onClick={submit} style={{ padding: '7px 20px', background: '#976793', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Add to order</button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Item Modal (create item & add to ItemMaster) ─────────────────────────
function EditItemModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name:'',itemCategory:'',uom:'unit',gst:0,type:'Item',description:'',rates:[{makeName:'Standard',clientRate:0,purchaseRate:0},{makeName:'',clientRate:0,purchaseRate:0},{makeName:'',clientRate:0,purchaseRate:0}] });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const iS = { width:'100%',padding:'7px 10px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:12,outline:'none',background:'#F9FAFB',fontFamily:'inherit' };
  const lS = { display:'block',fontSize:11,fontWeight:600,color:'#374151',marginBottom:3 };

  const h = (e) => setForm(f=>({...f,[e.target.name]:e.target.value}));
  const updateRate = (i,field,val) => setForm(f=>{ const r=[...f.rates]; r[i]={...r[i],[field]:val}; return {...f,rates:r}; });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v])=>{ if(k==='rates') fd.append(k,JSON.stringify(v)); else fd.append(k,v); });
      const r = await api.post('/item-master',fd,{headers:{'Content-Type':'multipart/form-data'}});
      onCreated(r.data.data.item); onClose();
    } catch(err) { alert(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1001,padding:16,overflowY:'auto' }}>
      <div style={{ background:'#fff',borderRadius:12,width:'100%',maxWidth:500,boxShadow:'0 20px 60px rgba(0,0,0,0.2)',margin:'auto' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:'1px solid #E5E7EB' }}>
          <h3 style={{ margin:0,fontSize:15,fontWeight:700 }}>Edit Item</h3>
          <button onClick={onClose} style={{ background:'none',border:'none',fontSize:18,cursor:'pointer',color:'#9CA3AF' }}>×</button>
        </div>
        <form onSubmit={submit} style={{ padding:'16px 20px',maxHeight:'75vh',overflowY:'auto' }}>
          <div style={{ marginBottom:10 }}><label style={lS}>Item Code</label><input style={iS} name="itemCode" placeholder="Auto-generated" /></div>
          <div style={{ marginBottom:10 }}>
            <label style={lS}>Item Category</label>
            <div style={{ display:'flex',gap:4 }}>
              <input style={{...iS,flex:1}} name="itemCategory" value={form.itemCategory} onChange={h} />
              <button type="button" style={{ padding:'4px 10px',background:'#976793',color:'#fff',border:'none',borderRadius:6,fontSize:11,cursor:'pointer',whiteSpace:'nowrap' }}>+ Item Category</button>
            </div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10 }}>
            <div><label style={lS}>Internal Finish</label>
              <div style={{ display:'flex',gap:4 }}>
                <select style={{...iS,flex:1}}><option value="">Select…</option>{['Laminate','PU','Veneer','Paint'].map(f=><option key={f}>{f}</option>)}</select>
                <button type="button" style={{ padding:'4px 8px',background:'#976793',color:'#fff',border:'none',borderRadius:6,fontSize:10,cursor:'pointer' }}>+ Internal</button>
              </div>
            </div>
            <div><label style={lS}>External Finish</label>
              <div style={{ display:'flex',gap:4 }}>
                <select style={{...iS,flex:1}}><option value="">Select…</option>{['Laminate','PU','Veneer','Paint'].map(f=><option key={f}>{f}</option>)}</select>
                <button type="button" style={{ padding:'4px 8px',background:'#976793',color:'#fff',border:'none',borderRadius:6,fontSize:10,cursor:'pointer' }}>+ External</button>
              </div>
            </div>
          </div>
          <div style={{ marginBottom:10 }}><label style={lS}>Name *</label><input style={iS} name="name" value={form.name} onChange={h} required /></div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10 }}>
            <div><label style={lS}>Unit Of Measurement *</label>
              <div style={{ display:'flex',gap:4 }}>
                <select style={{...iS,flex:1}} name="uom" value={form.uom} onChange={h}>
                  {UOM_OPTS.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
                <button type="button" style={{ padding:'4px 8px',background:'#976793',color:'#fff',border:'none',borderRadius:6,fontSize:10,cursor:'pointer' }}>+ UOM</button>
              </div>
            </div>
            <div><label style={lS}>GST *</label>
              <select style={iS} name="gst" value={form.gst} onChange={h}>
                {[0,5,12,18,28].map(g=><option key={g} value={g}>{g}%</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10 }}>
            <div><label style={lS}>Tag</label>
              <div style={{ display:'flex',gap:4 }}>
                <input style={{...iS,flex:1}} name="tag" onChange={h} />
                <button type="button" style={{ padding:'4px 8px',background:'#976793',color:'#fff',border:'none',borderRadius:6,fontSize:10,cursor:'pointer' }}>+ Tag</button>
              </div>
            </div>
            <div><label style={lS}>Type *</label>
              <select style={iS} name="type" value={form.type} onChange={h}>
                {['Item','Service','Material'].map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom:10 }}><label style={lS}>Description</label><textarea style={{...iS,minHeight:50,resize:'vertical'}} name="description" onChange={h} /></div>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:12,fontWeight:700,color:'#1F2937',marginBottom:8 }}>Rates</div>
            {form.rates.map((rate,i)=>(
              <div key={i} style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:6 }}>
                <div><label style={lS}>Make Name {i===0&&'*'}</label><input style={iS} value={rate.makeName} onChange={e=>updateRate(i,'makeName',e.target.value)} required={i===0} /></div>
                <div><label style={lS}>Client Rate</label><input type="number" style={iS} value={rate.clientRate} onChange={e=>updateRate(i,'clientRate',e.target.value)} /></div>
                <div><label style={lS}>Purchase Rate</label><input type="number" style={iS} value={rate.purchaseRate} onChange={e=>updateRate(i,'purchaseRate',e.target.value)} /></div>
              </div>
            ))}
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lS}>Image</label>
            <div style={{ display:'flex',alignItems:'center',gap:8 }}>
              <button type="button" onClick={()=>fileRef.current?.click()} style={{ padding:'5px 12px',background:'#976793',color:'#fff',border:'none',borderRadius:6,fontSize:11,cursor:'pointer' }}>☁ Upload</button>
              <span style={{ fontSize:11,color:'#9CA3AF' }}>No file selected</span>
              <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} />
            </div>
          </div>
          <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding:'7px 16px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:12,cursor:'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding:'7px 16px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer' }}>
              {saving?'Saving…':'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Order Editor (full-page) ───────────────────────────────────────────────────
function OrderEditor({ orderId, clientId, projectId, vendors, users, onClose, onSaved }) {
  const [form, setForm] = useState({ title:'', vendor:'', orderType:'', poc:'', deliveryDate:'', modeOfPayment:'', items:[], termsAndConditions:'' });
  const [localVendors, setLocalVendors] = useState(vendors || []);
  const [orderCode, setOrderCode] = useState('');
  const [showItems, setShowItems]     = useState(false);
  const [showEditItem, setShowEditItem] = useState(false);
  const [activeTab, setActiveTab]     = useState('items');
  const [showTermsMenu, setShowTermsMenu] = useState(false);
  const [showPaymentTermModal, setShowPaymentTermModal] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [openItemMenu, setOpenItemMenu] = useState(null);
  const [charges, setCharges] = useState([]);
  const [orderFiles, setOrderFiles] = useState([]);
  /** Files chosen before first save (new order); uploaded after create returns an id */
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingFilePreviews, setPendingFilePreviews] = useState([]);
  const pendingFilePreviewsRef = useRef([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [colFilterItem, setColFilterItem] = useState('');
  const [colFilterUom, setColFilterUom] = useState('');
  const fileRef = useRef();
  const [paymentTerms, setPaymentTerms] = useState([]);

  useEffect(() => {
    setLocalVendors(vendors || []);
  }, [vendors]);

  useEffect(() => {
    pendingFilePreviewsRef.current = pendingFilePreviews;
  }, [pendingFilePreviews]);

  useEffect(() => () => {
    pendingFilePreviewsRef.current.forEach((p) => URL.revokeObjectURL(p.url));
  }, []);

  useEffect(() => {
    const close = () => {
      setOpenItemMenu(null);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  useEffect(() => {
    if (orderId) {
      api.get(`/orders/${orderId}`).then(r=>{
        const o = r.data.data.order;
        setOrderCode(o.orderCode || '');
        setForm({
          title: o.title || '',
          vendor: o.vendor?._id || o.vendor || '',
          orderType: o.orderType || '',
          poc: o.poc?._id || o.poc || '',
          deliveryDate: o.deliveryDate ? new Date(o.deliveryDate).toISOString().split('T')[0] : '',
          modeOfPayment: o.modeOfPayment || '',
          items: (o.items || []).map((it) => ({
            ...it,
            _tmpId: Math.random().toString(36).slice(2),
            itemName: orderLineName(it) === '—' ? '' : orderLineName(it),
            lineDescription: orderLineDescription(it),
            materialMaster: it.materialMaster?._id || it.materialMaster || undefined,
            uom:
              it.uom != null && String(it.uom).trim() !== ''
                ? String(it.uom)
                : (it.materialMaster?.unit || it.item?.uom || 'unit'),
          })),
          termsAndConditions: o.termsAndConditions || '',
        });
        setCharges(
          Array.isArray(o.charges)
            ? o.charges.map((c) => ({
                ...c,
                _rowId: Math.random().toString(36).slice(2),
                _templateId: c._templateId,
              }))
            : []
        );
        setOrderFiles(Array.isArray(o.files) ? [...o.files] : []);
        setPendingFiles([]);
        setPendingFilePreviews((prev) => {
          prev.forEach((p) => URL.revokeObjectURL(p.url));
          return [];
        });
      }).catch(console.error);
    } else {
      setOrderCode('');
      setForm({ title:'', vendor:'', orderType:'', poc:'', deliveryDate:'', modeOfPayment:'', items:[], termsAndConditions:'' });
      setCharges([]);
      setOrderFiles([]);
      setPendingFiles([]);
      setPendingFilePreviews((prev) => {
        prev.forEach((p) => URL.revokeObjectURL(p.url));
        return [];
      });
      setPreviewFile(null);
    }
  }, [orderId]);

  const lineQty = (it) => {
    const q = it.qty;
    if (q === '' || q === undefined || q === null) return 0;
    const n = typeof q === 'number' ? q : parseFloat(String(q));
    return Number.isFinite(n) ? n : 0;
  };
  const lineRate = (it) => {
    const r = it.rate;
    if (r === '' || r === undefined || r === null) return 0;
    const n = typeof r === 'number' ? r : parseFloat(String(r));
    return Number.isFinite(n) ? n : 0;
  };
  const calcAmount = (it) => lineQty(it) * lineRate(it);
  const subTotal   = form.items.reduce((a,it)=>a+calcAmount(it),0);
  const gstTotal   = form.items.reduce((a,it)=>a+(calcAmount(it)*(it.gst||0)/100),0);
  const chargeAdj  = quoteChargesAdjustment(charges, subTotal);
  const grandTotal = Math.round((subTotal + gstTotal + chargeAdj) * 100) / 100;

  const filteredEditorItems = useMemo(() => {
    const f = { item: colFilterItem, section: '', uom: colFilterUom };
    if (!normalizeSearch(f.item) && !normalizeSearch(f.uom)) return form.items;
    return form.items.filter((it) => matchesOrderLine(it, f));
  }, [form.items, colFilterItem, colFilterUom]);

  const vendorOptions = useMemo(
    () => [...localVendors].map((v) => v.displayName).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [localVendors]
  );
  const pocOptions = useMemo(
    () => [...users].map((u) => u.name).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [users]
  );
  const orderTypePickerLabels = useMemo(() => ORDER_TYPE_OPTIONS.map((o) => o.short), []);

  const vendorDisplay = localVendors.find((v) => v._id === form.vendor)?.displayName || '';
  const pocDisplay = users.find((u) => u._id === form.poc)?.name || '';
  const orderTypeDisplay = ORDER_TYPE_OPTIONS.find((o) => o.value === form.orderType)?.short || '';

  const applyOrderTypeValue = (value) => {
    if (value !== 'Procurement' && value !== 'Work Order') return;
    setForm((f) => ({
      ...f,
      orderType: value,
      ...(value === 'Work Order' ? { items: [] } : {}),
    }));
    if (value === 'Work Order') {
      setShowItems(false);
      setShowEditItem(false);
    }
    if (value === 'Procurement') {
      setCharges([]);
    }
  };

  const addVendorByName = async (name) => {
    const displayName = String(name || '').trim();
    if (!displayName) return;
    const existing = localVendors.find((v) => String(v.displayName || '').trim().toLowerCase() === displayName.toLowerCase());
    if (existing) {
      setForm((f) => ({ ...f, vendor: existing._id }));
      return;
    }
    const res = await api.post('/vendors', { displayName, legalName: displayName });
    const vendor = res.data?.data?.vendor;
    if (!vendor?._id) throw new Error('Vendor was created but no id was returned');
    setLocalVendors((prev) => (
      prev.some((v) => v._id === vendor._id) ? prev : [...prev, vendor]
    ));
    setForm((f) => ({ ...f, vendor: vendor._id }));
  };

  const applyOrderTypeLabel = (label) => {
    const n = String(label || '').trim().toLowerCase();
    if (!n) {
      setForm((f) => ({ ...f, orderType: '' }));
      setShowItems(false);
      setShowEditItem(false);
      return;
    }
    const opt = ORDER_TYPE_OPTIONS.find(
      (o) => o.short.toLowerCase() === n || o.label.toLowerCase() === n || o.value.toLowerCase() === n
    );
    if (!opt) return;
    applyOrderTypeValue(opt.value);
  };

  const newEmptyChargeRow = () => ({
    _rowId: Math.random().toString(36).slice(2),
    chargeName: '',
    type: 'Labour',
    labourType: '',
    option: 'Credit',
    context: 'Orders',
    value: 0,
  });

  const addLabourLine = () => setCharges((ch) => [...ch, newEmptyChargeRow()]);

  const updateChargeRow = (rowId, patch) => {
    setCharges((ch) => ch.map((c) => (c._rowId === rowId ? { ...c, ...patch } : c)));
  };

  const removeChargeRow = (rowId) => setCharges((ch) => ch.filter((c) => c._rowId !== rowId));

  const handleOrderFileInput = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const previews = files.map((file) => ({
      file,
      name: file.name || 'Attachment',
      type: file.type || '',
      url: URL.createObjectURL(file),
    }));
    if (!orderId) {
      setPendingFiles((p) => [...p, ...files]);
      setPendingFilePreviews((p) => [...p, ...previews]);
      return;
    }
    try {
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append('file', files[i]);
        await api.post(`/orders/${orderId}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      previews.forEach((p) => URL.revokeObjectURL(p.url));
      const r = await api.get(`/orders/${orderId}`);
      setOrderFiles(r.data?.data?.order?.files || []);
    } catch (err) {
      previews.forEach((p) => URL.revokeObjectURL(p.url));
      alert(err.response?.data?.message || 'Upload failed');
    }
  };

  const removePendingFile = (index) => {
    setPendingFiles((p) => p.filter((_, i) => i !== index));
    setPendingFilePreviews((p) => {
      const removed = p[index];
      if (removed?.url) URL.revokeObjectURL(removed.url);
      if (previewFile?.url && removed?.url === previewFile.url) setPreviewFile(null);
      return p.filter((_, i) => i !== index);
    });
  };

  const handleAddPurchaseMaterials = (materialRows) => {
    const newItems = materialRows.map((m) => ({
      _tmpId: Math.random().toString(36).slice(2),
      materialMaster: m._id,
      itemName: m.name,
      lineDescription: m.description || '',
      section: '',
      qty: 1,
      uom: (m.unit && String(m.unit).trim()) ? String(m.unit).trim() : 'nos',
      rate: 0,
      gst: 0,
      amount: 0,
      remarks: '',
    }));
    setForm((f) => ({ ...f, items: [...f.items, ...newItems] }));
  };

  const handleCreatedItem = (mi) => {
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          _tmpId: Math.random().toString(36).slice(2),
          item: mi._id,
          itemName: mi.name,
          section: '',
          qty: 1,
          uom: UOM_OPTS.includes(mi.uom) ? mi.uom : 'unit',
          rate: 0,
          gst: mi.gst || 0,
          amount: 0,
          remarks: '',
        },
      ],
    }));
  };

  const updateItem = (tmpId, field, val) =>
    setForm((f) => ({ ...f, items: f.items.map((it) => (it._tmpId === tmpId ? { ...it, [field]: val } : it)) }));
  const removeItem = (tmpId) => setForm((f) => ({ ...f, items: f.items.filter((it) => it._tmpId !== tmpId) }));

  const reorderItemBeforeTarget = useCallback((sourceTmpId, targetTmpId) => {
    if (!sourceTmpId || sourceTmpId === targetTmpId) return;
    setForm((prev) => {
      const srcIdx = prev.items.findIndex((x) => x._tmpId === sourceTmpId);
      const dstIdx = prev.items.findIndex((x) => x._tmpId === targetTmpId);
      if (srcIdx < 0 || dstIdx < 0) return prev;
      const next = [...prev.items];
      const [row] = next.splice(srcIdx, 1);
      next.splice(dstIdx, 0, row);
      return { ...prev, items: next };
    });
  }, []);

  const moveItem = (tmpId, dir) => {
    setForm((f) => {
      const idx = f.items.findIndex((it) => it._tmpId === tmpId);
      if (idx < 0) return f;
      const next = [...f.items];
      if (dir === 'up' && idx > 0) [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      if (dir === 'down' && idx < next.length - 1) [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return { ...f, items: next };
    });
  };

  const handleSave = async () => {
    if (form.orderType !== 'Procurement' && form.orderType !== 'Work Order') {
      alert('Please select an order type: Procurement or Work order.');
      return;
    }
    if (form.orderType === 'Work Order') {
      const bad = charges.some((c) => (Number(c.value) || 0) !== 0 && !String(c.chargeName || '').trim());
      if (bad) {
        alert('For each line with a charge amount, enter a description.');
        return;
      }
    }
    setSaving(true);
    try {
      const cleanCharges = (form.orderType === 'Work Order' ? charges : []).map(({ _rowId, _templateId: _tid, ...c }) => ({
        chargeName: c.chargeName || '',
        context: c.context || 'Orders',
        option: c.option || 'Credit',
        type: c.type || 'Labour',
        labourType: c.labourType || c.chargeName || '',
        value: Number(c.value) || 0,
      })).filter((c) => String(c.chargeName || '').trim() !== '' || (Number(c.value) || 0) !== 0);

      const payload = {
        ...form,
        client: clientId,
        ...(projectId ? { project: projectId } : {}),
        charges: cleanCharges,
        files: orderFiles,
        items:
          form.orderType === 'Work Order'
            ? []
            : form.items.map(({ _tmpId, item, materialMaster, ...rest }) => {
                const row = {
                  ...rest,
                  qty: lineQty({ qty: rest.qty }) || 1,
                  rate: lineRate({ rate: rest.rate }),
                  amount: lineQty({ qty: rest.qty }) * lineRate({ rate: rest.rate }),
                  item: item?._id || item || undefined,
                  materialMaster: materialMaster?._id || materialMaster || undefined,
                };
                if (!row.item) delete row.item;
                if (!row.materialMaster) delete row.materialMaster;
                return row;
              }),
      };
      let targetId = orderId;
      if (orderId) {
        await api.put(`/orders/${orderId}`, payload);
      } else {
        const created = await api.post('/orders', payload);
        targetId = created.data?.data?.order?._id;
      }
      if (targetId && pendingFiles.length) {
        for (let i = 0; i < pendingFiles.length; i++) {
          const fd = new FormData();
          fd.append('file', pendingFiles[i]);
          await api.post(`/orders/${targetId}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        setPendingFiles([]);
        setPendingFilePreviews((prev) => {
          prev.forEach((p) => URL.revokeObjectURL(p.url));
          return [];
        });
        const refreshed = await api.get(`/orders/${targetId}`);
        setOrderFiles(refreshed.data?.data?.order?.files || []);
      }
      onSaved();
      onClose();
    } catch(e) { alert(e.response?.data?.message||'Save failed'); }
    finally { setSaving(false); }
  };

  // Section summary
  const sections = {};
  form.items.forEach(it=>{ const s=it.section||'No Section'; if(!sections[s]) sections[s]={items:0,base:0,disc:0,sub:0}; const base=calcAmount(it); sections[s].items++; sections[s].base+=base; sections[s].sub+=base; });

  const iS = { padding:'5px 7px',border:'1px solid #E5E7EB',borderRadius:6,fontSize:12,outline:'none',background:'#F9FAFB',fontFamily:'inherit',width:'100%' };
  const thS = { padding:'7px 8px',fontSize:11,fontWeight:600,color:'#6B7280',borderBottom:'2px solid #E5E7EB',textAlign:'left',background:'#fff',position:'sticky',top:0,zIndex:1 };
  const tdS = { padding:'5px 7px',borderBottom:'1px solid #F3F4F6',fontSize:12,verticalAlign:'middle' };

  return (
    <div style={{ display:'flex',flexDirection:'column',height:'100%',background:'#fff',fontFamily:'inherit' }}>
      {/* Header */}
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 20px',borderBottom:'1px solid #E5E7EB',position:'sticky',top:0,background:'#fff',zIndex:10 }}>
        <span style={{ fontSize:13,fontWeight:700,color:'#374151' }}>
          {orderId ? (orderCode || 'Edit Order') : 'New Order'}
        </span>
        <div style={{ display:'flex',gap:8 }}>
          <button onClick={onClose} style={{ padding:'6px 16px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:13,cursor:'pointer',color:'#374151' }}>Close</button>
          <button onClick={handleSave} disabled={saving}
            style={{ display:'flex',alignItems:'center',gap:5,padding:'6px 16px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            {saving?'Saving…':'Save'}
          </button>
        </div>
      </div>

      <div style={{ flex:1,overflowY:'auto',padding:'16px 20px' }}>
        {/* Top fields */}
        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))',gap:12,marginBottom:14 }}>
          <div style={{ minWidth: 0 }}>
            <label style={{ display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4 }}>Title</label>
            <input style={iS} value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="Order title" />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={{ display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4 }}>Vendor *</label>
            <SearchableSelectWithAdd
              value={vendorDisplay}
              onChange={(label) => {
                const v = localVendors.find((x) => x.displayName === label);
                setForm((f) => ({ ...f, vendor: v?._id || '' }));
              }}
              options={vendorOptions}
              onAddNew={addVendorByName}
              addPrompt="as vendor"
              placeholder="Search or type vendor…"
              allowEmptyCommit
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={{ display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4 }}>Order Type *</label>
            <SearchableSelectWithAdd
              value={orderTypeDisplay}
              onChange={(label) => applyOrderTypeLabel(label)}
              options={orderTypePickerLabels}
              placeholder="Search or type order type…"
              allowEmptyCommit
              style={{ width: '100%' }}
            />
            <div style={{ fontSize:10,color:'#9CA3AF',marginTop:4,lineHeight:1.35 }}>
              Procurement is for vendor material lines. Work order adds invoice-style charges (e.g. carpenter); those amounts roll into Financials under labour.
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={{ display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4 }}>POC</label>
            <SearchableSelectWithAdd
              value={pocDisplay}
              onChange={(name) => {
                const u = users.find((x) => x.name === name);
                setForm((f) => ({ ...f, poc: u?._id || '' }));
              }}
              options={pocOptions}
              placeholder="Search or type POC…"
              allowEmptyCommit
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))',gap:12,marginBottom:14 }}>
          <div>
            <label style={{ display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4 }}>Date of Delivery</label>
            <input type="date" style={iS} value={form.deliveryDate} onChange={e=>setForm(f=>({...f,deliveryDate:e.target.value}))} />
          </div>
          <div>
            <label style={{ display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4 }}>Mode of Payment</label>
            <select style={iS} value={form.modeOfPayment} onChange={e=>setForm(f=>({...f,modeOfPayment:e.target.value}))}>
              <option value="">Select MOP</option>
              {MODE_OF_PAYMENT_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex',borderBottom:'1px solid #E5E7EB',marginBottom:0 }}>
          {[['items','Items'],['section_summary','Section Summary']].map(([key,label])=>(
            <button key={key} onClick={()=>setActiveTab(key)}
              style={{ padding:'7px 14px',fontSize:13,fontWeight:activeTab===key?600:400,color:activeTab===key?'#976793':'#6B7280',background:'transparent',border:'none',borderBottom:activeTab===key?'2px solid #976793':'2px solid transparent',cursor:'pointer',marginBottom:-1 }}>
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'section_summary' && form.orderType === 'Work Order' && (
          <div>
            <p style={{ fontSize:12,color:'#6B7280',margin:'8px 0 10px' }}>Summary of work order lines (same as Items). Approved totals flow to Financials under labour.</p>
            <table style={{ width:'100%',borderCollapse:'collapse',marginTop:8 }}>
              <thead>
                <tr>
                  <th style={thS}>Description</th>
                  <th style={{ ...thS, textAlign:'right' }}>Charge (₹)</th>
                </tr>
              </thead>
              <tbody>
                {charges.filter((c) => String(c.chargeName || '').trim()).length === 0 ? (
                  <tr><td colSpan={2} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:12 }}>Add lines under the Items tab</td></tr>
                ) : (
                  charges
                    .filter((c) => String(c.chargeName || '').trim())
                    .map((c) => (
                      <tr key={c._rowId}>
                        <td style={tdS}>{c.chargeName}</td>
                        <td style={{ ...tdS, textAlign:'right', fontWeight:600 }}>₹{formatInr(Number(c.value) || 0)}</td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
            <div style={{ textAlign:'right',padding:'10px 0',fontSize:12,color:'#6B7280' }}>
              <div>Labour / charges: <strong>₹{formatInr(chargeAdj)}</strong></div>
              <div style={{ fontWeight:700,color:'#1F2937' }}>Total: ₹{formatInr(grandTotal)}</div>
            </div>
          </div>
        )}

        {activeTab === 'section_summary' && !form.orderType && (
          <div style={{ padding:'2rem', textAlign:'center', color:'#9CA3AF', fontSize:12 }}>
            Select <strong>Procurement</strong> or <strong>Work order</strong> above to see section summary.
          </div>
        )}

        {activeTab === 'section_summary' && form.orderType === 'Procurement' && (
          <div>
            <table style={{ width:'100%',borderCollapse:'collapse',marginTop:8 }}>
              <thead><tr>
                {['Section','Items','Base Total','Discount','Sub Total'].map(h=><th key={h} style={thS}>{h}</th>)}
              </tr></thead>
              <tbody>
                {Object.keys(sections).length===0 ? (
                  <tr><td colSpan={5} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:12 }}>No sections found</td></tr>
                ) : Object.entries(sections).map(([sec,d])=>(
                  <tr key={sec}>
                    <td style={tdS}>{sec}</td>
                    <td style={tdS}>{d.items}</td>
                    <td style={tdS}>₹ {formatInr(d.base)}</td>
                    <td style={tdS}>₹ {formatInr(0)}</td>
                    <td style={{ ...tdS,textAlign:'right' }}>₹ {formatInr(d.sub)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ textAlign:'right',padding:'10px 0',fontSize:12,color:'#6B7280' }}>
              <div>Sub Total: ₹{formatInr(subTotal)}</div>
              {gstTotal > 0 ? <div>GST: ₹{formatInr(gstTotal)}</div> : null}
              <div style={{ fontWeight:700,color:'#1F2937' }}>Total: ₹{formatInr(grandTotal)}</div>
            </div>
          </div>
        )}

        {activeTab === 'items' && !form.orderType && (
          <div style={{ padding:'2rem', textAlign:'center', color:'#9CA3AF', fontSize:12, marginBottom:14 }}>
            Select <strong>Procurement</strong> for material lines from Material Master, or <strong>Work order</strong> for labour charges.
          </div>
        )}

        {activeTab === 'items' && form.orderType === 'Work Order' && (
          <>
            <p style={{ fontSize:12,color:'#6B7280',margin:'0 0 10px' }}>
              Enter a description and charge amount for each line. Totals roll into <strong>Labour</strong> in Financials after this order is approved.
            </p>
            <div style={{ overflowX:'auto',border:'1px solid #E5E7EB',borderRadius:8,marginBottom:10,minHeight:180,paddingBottom:12 }}>
              <table style={{ width:'100%',borderCollapse:'collapse',minWidth:520 }}>
                <thead>
                  <tr>
                    <th style={thS}>Sr.No.</th>
                    <th style={thS}>Description</th>
                    <th style={{ ...thS, textAlign:'right' }}>Charges amount (₹)</th>
                    <th style={thS}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:12 }}>No lines yet — use “+ Add order” below</td></tr>
                  ) : (
                    charges.map((c, idx) => (
                      <tr key={c._rowId}>
                        <td style={{ ...tdS, color:'#6B7280', fontSize:11, width:48 }}>{idx + 1}</td>
                        <td style={{ ...tdS, minWidth:220 }}>
                          <input
                            type="text"
                            value={c.chargeName ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              updateChargeRow(c._rowId, { chargeName: v, labourType: v });
                            }}
                            placeholder="Description"
                            style={{ ...iS, minWidth: 200 }}
                          />
                        </td>
                        <td style={{ ...tdS, width:120, textAlign:'right' }}>
                          <input
                            type="number"
                            min={0}
                            step={0.01}
                            value={c.value === '' || c.value === undefined ? '' : c.value}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '') { updateChargeRow(c._rowId, { value: '' }); return; }
                              updateChargeRow(c._rowId, { value: parseFloat(v) || 0 });
                            }}
                            style={{ ...iS, textAlign:'right', width:'100%', maxWidth:110 }}
                          />
                        </td>
                        <td style={{ ...tdS, width:72 }}>
                          <button type="button" onClick={() => removeChargeRow(c._rowId)} style={{ border:'none',background:'transparent',color:'#EF4444',cursor:'pointer',fontSize:12 }}>Remove</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ display:'flex',gap:8,marginBottom:14,flexWrap:'wrap' }}>
              <button type="button" onClick={addLabourLine} style={{ display:'flex',alignItems:'center',gap:5,padding:'6px 14px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer' }}>+ Add order</button>
            </div>
            {/* Totals */}
            <div style={{ display:'flex',justifyContent:'flex-end',marginBottom:14 }}>
              <div style={{ textAlign:'right',fontSize:12,color:'#6B7280' }}>
                {chargeAdj !== 0 ? <div>Labour / charges: <strong>₹{formatInr(chargeAdj)}</strong></div> : <div>Labour / charges: <strong>₹{formatInr(0)}</strong></div>}
                <div style={{ fontWeight:700,fontSize:14,color:'#1F2937' }}>Total: ₹{formatInr(grandTotal)}</div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'items' && form.orderType === 'Procurement' && (
          <>
            <div style={{ overflowX:'auto',border:'1px solid #E5E7EB',borderRadius:8,marginBottom:10 }}>
              <table style={{ width:'100%',borderCollapse:'collapse',minWidth:640 }}>
                <thead>
                  <tr>
                    <th style={thS}>Sr.No.</th>
                    <th style={thS}>Item</th>
                    <th style={thS}>UOM</th>
                    <th style={thS}>Qty</th>
                    <th style={thS}>Rate</th>
                    <th style={{ ...thS, textAlign:'right' }}>Amount</th>
                    <th style={thS}>Action</th>
                  </tr>
                  <tr style={{ background:'#FAFAFA' }}>
                    <th style={{ ...thS, paddingTop:4, paddingBottom:8 }} />
                    <th style={{ ...thS, paddingTop:4, paddingBottom:8, verticalAlign:'top' }}>
                      <ColumnFilterInput value={colFilterItem} onChange={(e) => setColFilterItem(e.target.value)} placeholder="Filter item…" aria-label="Filter by item" />
                    </th>
                    <th style={{ ...thS, paddingTop:4, paddingBottom:8, verticalAlign:'top' }}>
                      <ColumnFilterInput value={colFilterUom} onChange={(e) => setColFilterUom(e.target.value)} placeholder="Filter UOM…" aria-label="Filter by UOM" />
                    </th>
                    <th style={{ ...thS, paddingTop:4 }} colSpan={4} />
                  </tr>
                </thead>
                <tbody>
                  {form.items.length===0 ? (
                    <tr><td colSpan={7} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:12 }}>Please add items from Material Master</td></tr>
                  ) : filteredEditorItems.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:12 }}>No lines match filters</td></tr>
                  ) : filteredEditorItems.map((item) => (
                    <tr
                      key={item._tmpId}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const srcId = e.dataTransfer.getData(DRAG_MIME_ORDER);
                        reorderItemBeforeTarget(srcId, item._tmpId);
                      }}
                    >
                      <td
                        draggable
                        title="Drag to reorder"
                        onDragStart={(e) => {
                          e.dataTransfer.setData(DRAG_MIME_ORDER, item._tmpId);
                          e.dataTransfer.effectAllowed = 'move';
                          try {
                            const img = new Image();
                            img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
                            e.dataTransfer.setDragImage(img, 0, 0);
                          } catch { /* ignore */ }
                        }}
                        style={{ ...tdS, color:'#6B7280', fontSize:11, width:56, cursor:'grab', userSelect:'none', verticalAlign:'middle' }}
                      >
                        <span style={{ marginRight:6, color:'#9CA3AF', letterSpacing:'-2px' }}>⠿</span>
                        {form.items.findIndex((x) => x._tmpId === item._tmpId) + 1}
                      </td>
                      <td style={{ ...tdS, minWidth:220 }}>
                        <div style={{ fontSize:12, fontWeight:600, color:'#1F2937', marginBottom:4 }}>{orderLineName(item)}</div>
                        <textarea
                          value={item.lineDescription ?? ''}
                          onChange={(e) => updateItem(item._tmpId, 'lineDescription', e.target.value)}
                          rows={2}
                          placeholder="Description…"
                          style={{ width:'100%', padding:'6px 8px', border:'1px solid #E5E7EB', borderRadius:6, fontSize:11, resize:'vertical', outline:'none', fontFamily:'inherit', background:'#F9FAFB', color:'#374151', minHeight:40, boxSizing:'border-box' }}
                        />
                      </td>
                      <td style={{ ...tdS, width:88 }}>
                        <input
                          list={`order-uom-${item._tmpId}`}
                          value={item.uom ?? ''}
                          onChange={(e) => updateItem(item._tmpId, 'uom', e.target.value)}
                          placeholder="e.g. nos"
                          style={{ ...iS, minWidth:72, boxSizing:'border-box', color:'#111827' }}
                        />
                        <datalist id={`order-uom-${item._tmpId}`}>
                          {UOM_OPTS.map((u) => (
                            <option key={u} value={u} />
                          ))}
                        </datalist>
                      </td>
                      <td style={{ ...tdS, width:88 }}>
                        <input
                          style={{ ...iS, width:'100%', minWidth:72, boxSizing:'border-box' }}
                          type="text"
                          inputMode="decimal"
                          value={item.qty === '' || item.qty === undefined || item.qty === null ? '' : String(item.qty)}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === '') { updateItem(item._tmpId, 'qty', ''); return; }
                            if (/^\d*\.?\d*$/.test(v)) updateItem(item._tmpId, 'qty', v);
                          }}
                          onBlur={() => {
                            const q = lineQty(item);
                            updateItem(item._tmpId, 'qty', q > 0 ? q : 1);
                          }}
                        />
                      </td>
                      <td style={{ ...tdS, width:100 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:2 }}>
                          <span style={{ color:'#9CA3AF', fontSize:11 }}>₹</span>
                          <input
                            style={{ ...iS, flex:1, minWidth:64, boxSizing:'border-box' }}
                            type="text"
                            inputMode="decimal"
                            value={item.rate === '' || item.rate === undefined || item.rate === null ? '' : String(item.rate)}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '') { updateItem(item._tmpId, 'rate', ''); return; }
                              if (/^\d*\.?\d*$/.test(v)) updateItem(item._tmpId, 'rate', v);
                            }}
                            onBlur={() => {
                              const r = lineRate(item);
                              updateItem(item._tmpId, 'rate', r >= 0 ? r : 0);
                            }}
                          />
                        </div>
                      </td>
                      <td style={{ ...tdS, textAlign:'right', fontWeight:600, width:96, color:'#1F2937' }}>₹{formatInr(calcAmount(item))}</td>
                      <td style={{ ...tdS, width:40, position:'relative' }}>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setOpenItemMenu((m) => (m === item._tmpId ? null : item._tmpId)); }}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', padding:2 }}>⋮</button>
                        {openItemMenu===item._tmpId && (
                          <div style={{ position:'absolute', right:0, top:28, background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, boxShadow:'0 4px 20px rgba(0,0,0,0.1)', zIndex:100, overflow:'hidden', minWidth:130 }}>
                            {[
                              ['Remove', () => removeItem(item._tmpId)],
                              ['Move Up', () => moveItem(item._tmpId, 'up')],
                              ['Move Down', () => moveItem(item._tmpId, 'down')],
                            ].map(([label, fn]) => (
                              <button
                                key={label}
                                type="button"
                                onClick={() => { fn(); setOpenItemMenu(null); }}
                                style={{ display:'block', width:'100%', textAlign:'left', padding:'7px 12px', border:'none', background:'transparent', fontSize:11, cursor:'pointer', color: label === 'Remove' ? '#EF4444' : '#374151' }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#F9FAFB'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                {label === 'Move Up' && '↑ '}{label === 'Move Down' && '↓ '}{label}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display:'flex',gap:8,marginBottom:14,flexWrap:'wrap' }}>
              <button type="button" onClick={()=>setShowItems(true)} style={{ display:'flex',alignItems:'center',gap:5,padding:'6px 14px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer' }}>+ Add Items</button>
              <button type="button" onClick={()=>setShowEditItem(true)} style={{ display:'flex',alignItems:'center',gap:5,padding:'6px 14px',background:'#fff',color:'#374151',border:'1px solid #E5E7EB',borderRadius:8,fontSize:12,cursor:'pointer' }}>+ Create Item</button>
            </div>

            {/* Totals */}
            <div style={{ display:'flex',justifyContent:'flex-end',marginBottom:14 }}>
              <div style={{ textAlign:'right',fontSize:12,color:'#6B7280' }}>
                <div>Sub Total: <strong>₹{formatInr(subTotal)}</strong></div>
                {gstTotal > 0 ? <div>GST: <strong>₹{formatInr(gstTotal)}</strong></div> : null}
                <div style={{ fontWeight:700,fontSize:14,color:'#1F2937' }}>Total: ₹{formatInr(grandTotal)}</div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'items' && (
          <>
            {/* Select Files */}
            <div style={{ marginBottom:14 }} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize:12,fontWeight:600,color:'#374151',marginBottom:4 }}>Select Files</div>
              {orderFiles.length === 0 && pendingFiles.length === 0 ? (
                <div style={{ fontSize:11,color:'#9CA3AF',marginBottom:6 }}>No files attached</div>
              ) : null}
              {orderFiles.length > 0 && (
                <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))',gap:8,margin:'0 0 8px' }}>
                  {orderFiles.map((f, i) => (
                    <div key={`${f}-${i}`} style={{ border:'1px solid #E5E7EB',borderRadius:8,overflow:'hidden',background:'#fff' }}>
                      <button type="button" onClick={() => setPreviewFile({ name: orderFileName(f), url: orderFileUrl(f), source: 'uploaded' })} style={{ width:'100%',height:82,border:'none',background:'#F9FAFB',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',padding:0,overflow:'hidden' }}>
                        {isOrderImage(f) ? (
                          <img src={orderFileUrl(f)} alt={orderFileName(f)} style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                        ) : (
                          <span style={{ fontSize:12,fontWeight:800,color:isOrderPdf(f) ? '#EF4444' : '#6B7280' }}>{isOrderPdf(f) ? 'PDF' : 'FILE'}</span>
                        )}
                      </button>
                      <div style={{ padding:'6px 8px' }}>
                        <div title={orderFileName(f)} style={{ fontSize:11,fontWeight:600,color:'#374151',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{orderFileName(f)}</div>
                        <div style={{ display:'flex',gap:8,marginTop:4 }}>
                          <button type="button" onClick={() => setPreviewFile({ name: orderFileName(f), url: orderFileUrl(f), source: 'uploaded' })} style={{ border:'none',background:'transparent',color:'#976793',fontSize:10,fontWeight:700,cursor:'pointer',padding:0 }}>Preview</button>
                          <a href={orderFileUrl(f)} target="_blank" rel="noreferrer" style={{ color:'#6B7280',fontSize:10,textDecoration:'none' }}>Open</a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {pendingFiles.length > 0 && (
                <div style={{ margin:'0 0 8px',fontSize:11,color:'#92400E' }}>
                  <div style={{ fontWeight:600,marginBottom:4 }}>Queued until first save ({pendingFiles.length})</div>
                  <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))',gap:8,color:'#374151' }}>
                    {pendingFiles.map((file, i) => (
                      <div key={`${file.name}-${i}-${file.lastModified}`} style={{ border:'1px solid #E5E7EB',borderRadius:8,overflow:'hidden',background:'#fff' }}>
                        <button type="button" onClick={() => setPreviewFile(pendingFilePreviews[i])} style={{ width:'100%',height:82,border:'none',background:'#F9FAFB',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',padding:0,overflow:'hidden' }}>
                          {isOrderImage(file) && pendingFilePreviews[i]?.url ? (
                            <img src={pendingFilePreviews[i].url} alt={file.name} style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                          ) : (
                            <span style={{ fontSize:12,fontWeight:800,color:isOrderPdf(file) ? '#EF4444' : '#6B7280' }}>{isOrderPdf(file) ? 'PDF' : 'FILE'}</span>
                          )}
                        </button>
                        <div style={{ padding:'6px 8px' }}>
                          <div title={file.name} style={{ fontSize:11,fontWeight:600,color:'#374151',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{file.name}</div>
                          <div style={{ display:'flex',gap:8,marginTop:4 }}>
                            <button type="button" onClick={() => setPreviewFile(pendingFilePreviews[i])} style={{ border:'none',background:'transparent',color:'#976793',fontSize:10,fontWeight:700,cursor:'pointer',padding:0 }}>Preview</button>
                            <button type="button" onClick={() => removePendingFile(i)} style={{ border:'none',background:'transparent',color:'#EF4444',cursor:'pointer',fontSize:10,padding:0 }}>Remove</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button type="button" onClick={(ev) => { ev.stopPropagation(); fileRef.current?.click(); }} style={{ display:'flex',alignItems:'center',gap:5,padding:'5px 12px',background:'#976793',color:'#fff',border:'none',borderRadius:6,fontSize:11,cursor:'pointer' }}>
                ☁ Upload
              </button>
              <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" style={{ display:'none' }} onChange={handleOrderFileInput} />
            </div>

            {/* Terms and conditions */}
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12,fontWeight:600,color:'#374151',marginBottom:4 }}>Terms and conditions</div>
              <div style={{ position:'relative',display:'inline-block' }}>
                <div style={{ display:'flex',alignItems:'center',border:'1px solid #E5E7EB',borderRadius:8,background:'#F9FAFB',overflow:'hidden' }}>
                  <select style={{ padding:'6px 10px',border:'none',background:'transparent',fontSize:12,outline:'none',minWidth:150,fontFamily:'inherit' }}>
                    <option value="">Select</option>
                    {paymentTerms.map(t=><option key={t.id}>{t.title}</option>)}
                  </select>
                  <button onClick={()=>setShowTermsMenu(m=>!m)} style={{ padding:'6px 8px',background:'none',border:'none',cursor:'pointer',color:'#9CA3AF' }}>▾</button>
                </div>
                {showTermsMenu && (
                  <div style={{ position:'absolute',top:36,left:0,background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,boxShadow:'0 4px 20px rgba(0,0,0,0.1)',zIndex:100,minWidth:200,overflow:'hidden' }}>
                    {paymentTerms.length===0 && <div style={{ padding:'10px 14px',fontSize:12,color:'#9CA3AF' }}>No data available</div>}
                    <button onClick={()=>{ setShowPaymentTermModal(true); setShowTermsMenu(false); }}
                      style={{ display:'flex',alignItems:'center',gap:6,width:'100%',padding:'9px 14px',border:'none',background:'#976793',color:'#fff',fontSize:12,cursor:'pointer',fontWeight:600 }}>
                      + Add Payment term
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {previewFile && (
        <div
          onClick={() => setPreviewFile(null)}
          style={{ position:'fixed',inset:0,zIndex:2000,background:'rgba(17,24,39,0.82)',display:'flex',alignItems:'center',justifyContent:'center',padding:24 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width:'min(920px, 94vw)',maxHeight:'90vh',background:'#fff',borderRadius:12,overflow:'hidden',boxShadow:'0 24px 70px rgba(0,0,0,0.28)' }}
          >
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,padding:'12px 16px',borderBottom:'1px solid #E5E7EB' }}>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:14,fontWeight:700,color:'#1F2937',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                  {orderFileName(previewFile)}
                </div>
                <div style={{ fontSize:11,color:'#9CA3AF' }}>
                  {isOrderImage(previewFile) ? 'Image preview' : isOrderPdf(previewFile) ? 'PDF preview' : 'File preview'}
                </div>
              </div>
              <div style={{ display:'flex',gap:8 }}>
                {orderFileUrl(previewFile) ? (
                  <a href={orderFileUrl(previewFile)} target="_blank" rel="noreferrer" style={{ padding:'7px 12px',border:'1px solid #E5E7EB',borderRadius:8,color:'#374151',textDecoration:'none',fontSize:12,fontWeight:600 }}>
                    Open
                  </a>
                ) : null}
                <button type="button" onClick={() => setPreviewFile(null)} style={{ width:32,border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',color:'#6B7280',fontSize:16,cursor:'pointer' }}>
                  x
                </button>
              </div>
            </div>
            <div style={{ height:'min(72vh, 680px)',background:'#F9FAFB',display:'flex',alignItems:'center',justifyContent:'center' }}>
              {isOrderImage(previewFile) ? (
                <img src={orderFileUrl(previewFile)} alt={orderFileName(previewFile)} style={{ maxWidth:'100%',maxHeight:'100%',objectFit:'contain' }} />
              ) : isOrderPdf(previewFile) ? (
                <iframe title={orderFileName(previewFile)} src={orderFileUrl(previewFile)} style={{ width:'100%',height:'100%',border:0 }} />
              ) : (
                <div style={{ textAlign:'center',color:'#6B7280',padding:24 }}>
                  <div style={{ fontSize:42,fontWeight:800,marginBottom:10 }}>FILE</div>
                  <div style={{ fontSize:13,marginBottom:12 }}>Preview is not available for this file type.</div>
                  {orderFileUrl(previewFile) ? (
                    <a href={orderFileUrl(previewFile)} target="_blank" rel="noreferrer" style={{ color:'#976793',fontWeight:700 }}>
                      Open file
                    </a>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showItems && form.orderType === 'Procurement' && <SelectItemsModal onClose={()=>setShowItems(false)} onAdd={handleAddPurchaseMaterials} />}
      {showEditItem && form.orderType === 'Procurement' && <EditItemModal onClose={()=>setShowEditItem(false)} onCreated={handleCreatedItem} />}
      {showPaymentTermModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1001,padding:16 }}>
          <div style={{ background:'#fff',borderRadius:12,width:'100%',maxWidth:380,boxShadow:'0 20px 60px rgba(0,0,0,0.15)',padding:20 }}>
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14 }}>
              <h3 style={{ margin:0,fontSize:15,fontWeight:700 }}>New Payment Term</h3>
              <button onClick={()=>setShowPaymentTermModal(false)} style={{ background:'none',border:'none',fontSize:18,cursor:'pointer',color:'#9CA3AF' }}>×</button>
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4 }}>Title *</label>
              <input style={{ width:'100%',padding:'8px 12px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit' }} />
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4 }}>Description</label>
              <textarea style={{ width:'100%',padding:'8px 12px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit',minHeight:70,resize:'vertical' }} />
            </div>
            <div style={{ display:'flex',justifyContent:'flex-end' }}>
              <button style={{ padding:'7px 20px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer' }}>Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Orders Tab ────────────────────────────────────────────────────────────
export default function OrdersTab({ clientId, projectId }) {
  const [orders, setOrders]   = useState([]);
  const [vendors, setVendors] = useState([]);
  const [users, setUsers]     = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState({});
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuWrapRef = useRef(null);
  /** Which order row’s ⋮ actions menu is open (order id) */
  const [orderMenuId, setOrderMenuId] = useState(null);
  const [orderPreviewFile, setOrderPreviewFile] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter]     = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [ordersListSearch, setOrdersListSearch] = useState('');
  const [serverExpenseSummary, setServerExpenseSummary] = useState(null);
  const [orderLineFiltersById, setOrderLineFiltersById] = useState({});

  const patchOrderLineFilters = (orderId, patch) => {
    setOrderLineFiltersById((prev) => ({
      ...prev,
      [orderId]: { ...emptyOrderLineFilters(), ...(prev[orderId] || {}), ...patch },
    }));
  };

  const load = useCallback(async () => {
    try {
      const params = { client: clientId, limit: 10000 };
      if (statusFilter) params.status    = statusFilter;
      if (typeFilter)   params.orderType = typeFilter;
      if (vendorFilter) params.vendor = vendorFilter;
      if (ordersListSearch.trim()) params.search = ordersListSearch.trim();
      const [oR, vR, uR] = await Promise.all([
        api.get('/orders', { params }),
        api.get('/vendors', { params: { limit: 200 } }),
        api.get('/users', { params: { limit: 100 } }).catch(() => ({ data: { data: { users: [] } } })),
      ]);
      const loadedOrders = oR.data.data.orders || [];
      setOrders(loadedOrders);
      if (oR.data.data.expenseSummary) {
        setServerExpenseSummary(oR.data.data.expenseSummary);
      }
      setVendors(vR.data.data.vendors || []);
      setUsers(uR.data.data.users || []);
    } catch(e) { console.error(e); }
  }, [clientId, statusFilter, typeFilter, vendorFilter, ordersListSearch]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setExpanded(null);
  }, [clientId, statusFilter, typeFilter, vendorFilter, ordersListSearch]);

  const filteredOrders = useMemo(() => {
    return orders;
  }, [orders]);

  const expenseSummary = useMemo(() => {
    if (serverExpenseSummary) {
      return serverExpenseSummary;
    }
    return filteredOrders.reduce((acc, order) => {
      const amount = Number(order?.total) || 0;
      if (order?.orderType === 'Work Order') {
        acc.workOrder += amount;
        acc.workOrderCount += 1;
      } else {
        acc.procurement += amount;
        acc.procurementCount += 1;
      }
      acc.total += amount;
      return acc;
    }, { procurement: 0, procurementCount: 0, workOrder: 0, workOrderCount: 0, total: 0 });
  }, [serverExpenseSummary, filteredOrders]);

  useEffect(() => {
    if (!showMoreMenu) return;
    const close = (e) => {
      if (moreMenuWrapRef.current && !moreMenuWrapRef.current.contains(e.target)) setShowMoreMenu(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showMoreMenu]);

  useEffect(() => {
    if (!orderMenuId) return;
    const close = (e) => {
      const wrap = document.querySelector(`[data-order-menu="${orderMenuId}"]`);
      if (wrap && !wrap.contains(e.target)) setOrderMenuId(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [orderMenuId]);

  useEffect(() => {
    setOrderMenuId(null);
  }, [expanded]);

  const downloadOrdersCsv = useCallback(() => {
    const rows = filteredOrders;
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Order code', 'Status', 'Type', 'Vendor', 'POC', 'Total'].join(',');
    const lines = rows.map((o) =>
      [o.orderCode, o.status, o.orderType, o.vendor?.displayName || '', o.poc?.name || '', o.total ?? ''].map(esc).join(',')
    );
    const blob = new Blob([`\ufeff${header}\n${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowMoreMenu(false);
  }, [filteredOrders]);

  const getSubTab = (id) => activeSubTab[id] || 'section_summary';
  const setSubTab = (id,tab) => setActiveSubTab(s=>({...s,[id]:tab}));

  // Build section summary for display
  const buildSections = (items=[]) => {
    const secs = {};
    items.forEach(it=>{ const s=it.section||'No Section'; if(!secs[s]) secs[s]={items:0,base:0}; secs[s].items++; secs[s].base+=(it.qty||1)*(it.rate||0); });
    return secs;
  };

  const thS = { padding:'7px 10px',fontSize:11,fontWeight:600,color:'#6B7280',borderBottom:'1px solid #E5E7EB',textAlign:'left',background:'#F9FAFB' };
  const tdS = { padding:'8px 10px',fontSize:12,color:'#374151',borderBottom:'1px solid #F3F4F6',verticalAlign:'middle' };
  const hasActiveOrderFilters = !!(statusFilter || typeFilter || vendorFilter || ordersListSearch.trim());

  if (showEditor) {
    return (
      <OrderEditor
        key={editOrderId || 'new'}
        orderId={editOrderId}
        clientId={clientId}
        projectId={projectId}
        vendors={vendors}
        users={users}
        onClose={()=>{ setShowEditor(false); setEditOrderId(null); }}
        onSaved={()=>{ load(); setShowEditor(false); setEditOrderId(null); }}
      />
    );
  }

  return (
    <div>
      {/* Filters + actions: two rows so + Order never wraps into a stray duplicate line (flexWrap + marginLeft:auto). */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <TableSearchInput
            value={ordersListSearch}
            onChange={(e) => setOrdersListSearch(e.target.value)}
            placeholder="Search orders (code, title, status...)"
            style={{ flex: '1 1 200px', minWidth: 180, marginBottom: 0 }}
          />
          <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}
            style={{ padding:'6px 10px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:12,outline:'none',background:'#fff',fontFamily:'inherit' }}>
            <option value="">Select Status</option>
            {['draft','approved','rejected','cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}
            style={{ padding:'6px 10px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:12,outline:'none',background:'#fff',fontFamily:'inherit' }}>
            <option value="">All order types</option>
            <option value="Work Order">Work order</option>
            <option value="Procurement">Procurement</option>
          </select>
          <select value={vendorFilter} onChange={e=>setVendorFilter(e.target.value)}
            style={{ padding:'6px 10px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:12,outline:'none',background:'#fff',fontFamily:'inherit' }}>
            <option value="">Select vendor</option>
            {vendors.map(v=><option key={v._id} value={v._id}>{v.displayName}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6 }}>
          <button type="button" onClick={()=>{ setEditOrderId(null); setShowEditor(true); }}
            style={{ display:'flex',alignItems:'center',gap:5,padding:'7px 14px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer' }}>
            + Order
          </button>
          <div ref={moreMenuWrapRef} style={{ position:'relative' }}>
            <button
              type="button"
              aria-label="More list actions"
              aria-expanded={showMoreMenu}
              onClick={(e) => { e.stopPropagation(); setShowMoreMenu((m) => !m); }}
              style={{ width:30,height:30,border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#9CA3AF' }}
            >
              ⋮
            </button>
            {showMoreMenu && (
              <div style={{ position:'absolute',right:0,top:34,background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,boxShadow:'0 4px 20px rgba(0,0,0,0.12)',zIndex:10060,overflow:'hidden',minWidth:180 }}>
                <button
                  type="button"
                  onClick={() => downloadOrdersCsv()}
                  style={{ display:'block',width:'100%',textAlign:'left',padding:'9px 14px',border:'none',background:'transparent',fontSize:12,cursor:'pointer',color:'#374151' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#F9FAFB'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  Download CSV (visible list)
                </button>
                <button
                  type="button"
                  onClick={() => { setShowMoreMenu(false); window.alert('Import from Excel is not configured yet.'); }}
                  style={{ display:'block',width:'100%',textAlign:'left',padding:'9px 14px',border:'none',background:'transparent',fontSize:12,cursor:'pointer',color:'#374151',borderTop:'1px solid #F3F4F6' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#F9FAFB'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  Import from Excel…
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))',gap:10,marginBottom:14 }}>
        <div style={{ border:'1px solid #E5E7EB',borderRadius:8,background:'#FFFBEB',padding:'10px 12px' }}>
          <div style={{ fontSize:10,fontWeight:800,color:'#92400E',textTransform:'uppercase',letterSpacing:'0.04em' }}>Till date spend</div>
          <div style={{ fontSize:18,fontWeight:800,color:'#1F2937',marginTop:4 }}>₹ {formatInr(expenseSummary.total)}</div>
          <div style={{ fontSize:11,color:'#6B7280',marginTop:3 }}>
            {hasActiveOrderFilters
              ? `Across ${orders.length} filtered order${orders.length === 1 ? '' : 's'}`
              : `Total across all ${orders.length} order${orders.length === 1 ? '' : 's'}`}
          </div>
        </div>
        <div style={{ border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',padding:'10px 12px' }}>
          <div style={{ fontSize:10,fontWeight:800,color:'#047857',textTransform:'uppercase',letterSpacing:'0.04em' }}>Procurement</div>
          <div style={{ fontSize:16,fontWeight:800,color:'#1F2937',marginTop:4 }}>₹ {formatInr(expenseSummary.procurement)}</div>
          <div style={{ fontSize:11,color:'#6B7280',marginTop:3 }}>{expenseSummary.procurementCount} order{expenseSummary.procurementCount === 1 ? '' : 's'}</div>
        </div>
        <div style={{ border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',padding:'10px 12px' }}>
          <div style={{ fontSize:10,fontWeight:800,color:'#2e1a46',textTransform:'uppercase',letterSpacing:'0.04em' }}>Work orders</div>
          <div style={{ fontSize:16,fontWeight:800,color:'#1F2937',marginTop:4 }}>₹ {formatInr(expenseSummary.workOrder)}</div>
          <div style={{ fontSize:11,color:'#6B7280',marginTop:3 }}>{expenseSummary.workOrderCount} order{expenseSummary.workOrderCount === 1 ? '' : 's'}</div>
        </div>
      </div>

      {/* Order rows */}
      {orders.length===0 && !hasActiveOrderFilters ? (
        <div style={{ textAlign:'center',padding:'4rem',color:'#9CA3AF',border:'1px solid #E5E7EB',borderRadius:10 }}>No orders yet</div>
      ) : orders.length === 0 ? (
        <div style={{ textAlign:'center',padding:'3rem',color:'#9CA3AF',border:'1px solid #E5E7EB',borderRadius:10,fontSize:13 }}>No orders match your filters</div>
      ) : filteredOrders.map(order=>(
        <div key={order._id} style={{ border:'1px solid #E5E7EB',borderRadius:10,marginBottom:10,overflow: expanded === order._id ? 'visible' : 'hidden' }}>
          {/* Row header */}
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 14px',background:'#fff',cursor:'pointer' }}
            onClick={()=>setExpanded(e=>e===order._id?null:order._id)}>
            <div style={{ display:'flex',alignItems:'center',gap:10,flex:1,flexWrap:'wrap' }}>
              <div style={{ width:28,height:28,borderRadius:6,background:'#FFF7ED',border:'1px solid #FED7AA',display:'flex',alignItems:'center',justifyContent:'center' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              </div>
              <span style={{ fontSize:13,fontWeight:700,color:'#1F2937' }}>
                {order.orderCode || 'TSO-Auto'}
              </span>
              {order.orderType && (
                <span style={{ background: order.orderType === 'Work Order' ? '#F7F1F7' : '#ECFDF5', color: order.orderType === 'Work Order' ? '#2e1a46' : '#047857', padding:'1px 8px', borderRadius:20, fontSize:10, fontWeight:700 }}>
                  {order.orderType === 'Work Order' ? 'Work order' : 'Procurement'}
                </span>
              )}
              <span style={{ background:'#F3F4F6',color:'#6B7280',padding:'1px 8px',borderRadius:20,fontSize:10,fontWeight:600,textTransform:'capitalize' }}>{order.status}</span>
              {order.vendor && <span style={{ fontSize:12,color:'#976793' }}>{order.vendor.displayName}</span>}
              {order.poc && <span style={{ fontSize:11,color:'#6B7280' }}>POC: {order.poc.name}</span>}
              {(order.files || []).length > 0 && (
                <button
                  type="button"
                  title={`Preview attachments (${order.files.length})`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOrderPreviewFile({ name: orderFileName(order.files[0]), url: orderFileUrl(order.files[0]), source: 'uploaded' });
                  }}
                  style={{ display:'inline-flex',alignItems:'center',gap:4,background:'#fff',border:'1px solid #E5E7EB',borderRadius:6,cursor:'pointer',color:'#976793',fontSize:11,padding:'2px 6px' }}
                >
                  Files {order.files.length}
                </button>
              )}
            </div>
            <div style={{ display:'flex',alignItems:'center',gap:8 }}>
              {order.total>0 && <span style={{ fontSize:13,fontWeight:700,color:'#1F2937' }}>₹ {formatInr(order.total)}</span>}
              <span style={{ color:'#9CA3AF',fontSize:14 }}>{expanded===order._id?'▲':'▼'}</span>
            </div>
          </div>
          <div style={{ display:'flex',gap:12,flexWrap:'wrap',fontSize:11,color:'#6B7280',padding:'0 14px 8px' }}>
            <span>Date of Delivery: <strong style={{ color:'#374151' }}>{fmtDateOnly(order.deliveryDate)}</strong></span>
            <span>MOP: <strong style={{ color:'#374151' }}>{order.modeOfPayment || 'Not set'}</strong></span>
          </div>
          {(() => {
            const highlights = orderHighlightItems(order);
            if (highlights.length === 0) return null;
            return (
              <div style={{ display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',padding:'0 14px 10px' }}>
                <span style={{ fontSize:10,fontWeight:800,color:'#92400E',textTransform:'uppercase',letterSpacing:'0.04em' }}>Items</span>
                {highlights.slice(0, 4).map((name, index) => (
                  <span key={`${name}-${index}`} style={{ maxWidth:190,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',background:'#FFFBEB',border:'1px solid #FDE68A',color:'#92400E',borderRadius:999,padding:'3px 9px',fontSize:11,fontWeight:700 }}>
                    {name}
                  </span>
                ))}
                {highlights.length > 4 ? (
                  <span style={{ background:'#F3F4F6',color:'#6B7280',borderRadius:999,padding:'3px 8px',fontSize:11,fontWeight:700 }}>
                    +{highlights.length - 4} more
                  </span>
                ) : null}
              </div>
            );
          })()}

          {/* Expanded */}
          {expanded===order._id && (
            <div style={{ borderTop:'1px solid #E5E7EB' }}>
              {/* Edit/Approve/PDF */}
              <div style={{ display:'flex',gap:8,padding:'10px 14px',background:'#FAFAFA',borderBottom:'1px solid #E5E7EB',justifyContent:'flex-end',alignItems:'center',position:'relative' }}>
                <button type="button" onClick={()=>{ setEditOrderId(order._id); setShowEditor(true); }}
                  style={{ display:'flex',alignItems:'center',gap:4,padding:'5px 12px',border:'1px solid #976793',borderRadius:8,background:'#fff',color:'#976793',fontSize:11,fontWeight:600,cursor:'pointer' }}>
                  ✏ Edit
                </button>
                {order.status!=='approved' && (
                  <button type="button" onClick={()=>api.patch(`/orders/${order._id}/status`,{status:'approved'}).then(load)}
                    style={{ display:'flex',alignItems:'center',gap:4,padding:'5px 12px',border:'1px solid #10B981',borderRadius:8,background:'#fff',color:'#10B981',fontSize:11,fontWeight:600,cursor:'pointer' }}>
                    ✓ Approve
                  </button>
                )}
                <button type="button" onClick={() => window.alert('PDF export for this order is not wired yet. Use Save from the editor if you need a printout.')}
                  style={{ display:'flex',alignItems:'center',gap:4,padding:'5px 12px',border:'1px solid #EF4444',borderRadius:8,background:'#fff',color:'#EF4444',fontSize:11,fontWeight:600,cursor:'pointer' }}>
                  📄 PDF
                </button>
                <div data-order-menu={order._id} style={{ position:'relative' }}>
                  <button
                    type="button"
                    aria-label="More order actions"
                    aria-expanded={orderMenuId === order._id}
                    onClick={(e) => { e.stopPropagation(); setOrderMenuId((id) => (id === order._id ? null : order._id)); }}
                    style={{ width:28,height:28,border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#9CA3AF' }}
                  >
                    ⋮
                  </button>
                  {orderMenuId === order._id && (
                    <div style={{ position:'absolute',right:0,top:'100%',marginTop:4,background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,boxShadow:'0 8px 24px rgba(0,0,0,0.12)',zIndex:10060,minWidth:168,overflow:'hidden' }}>
                      {order.status !== 'rejected' && order.status !== 'cancelled' && (
                        <button
                          type="button"
                          onClick={() => {
                            setOrderMenuId(null);
                            api.patch(`/orders/${order._id}/status`, { status: 'rejected' }).then(load).catch((err) => window.alert(err.response?.data?.message || 'Could not update status'));
                          }}
                          style={{ display:'block',width:'100%',textAlign:'left',padding:'9px 14px',border:'none',background:'transparent',fontSize:12,cursor:'pointer',color:'#B45309' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#FFFBEB'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          Mark rejected
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (!window.confirm(`Delete order ${order.orderCode || order._id}? This cannot be undone.`)) return;
                          setOrderMenuId(null);
                          api.delete(`/orders/${order._id}`)
                            .then(() => { load(); if (expanded === order._id) setExpanded(null); })
                            .catch((err) => window.alert(err.response?.data?.message || 'Delete failed (admin only).'));
                        }}
                        style={{ display:'block',width:'100%',textAlign:'left',padding:'9px 14px',border:'none',background:'transparent',fontSize:12,cursor:'pointer',color:'#EF4444',borderTop: order.status === 'rejected' || order.status === 'cancelled' ? 'none' : '1px solid #F3F4F6' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#FEF2F2'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        Delete order
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {(order.files || []).length > 0 && (
                <div style={{ padding:'10px 14px',background:'#fff',borderBottom:'1px solid #E5E7EB' }}>
                  <div style={{ fontSize:11,fontWeight:700,color:'#6B7280',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.04em' }}>Attachments</div>
                  <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))',gap:8 }}>
                    {order.files.map((f, i) => (
                      <div key={`${f}-${i}`} style={{ border:'1px solid #E5E7EB',borderRadius:8,overflow:'hidden',background:'#fff' }}>
                        <button
                          type="button"
                          onClick={() => setOrderPreviewFile({ name: orderFileName(f), url: orderFileUrl(f), source: 'uploaded' })}
                          style={{ width:'100%',height:82,border:'none',background:'#F9FAFB',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',padding:0,overflow:'hidden' }}
                        >
                          {isOrderImage(f) ? (
                            <img src={orderFileUrl(f)} alt={orderFileName(f)} style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                          ) : (
                            <span style={{ fontSize:12,fontWeight:800,color:isOrderPdf(f) ? '#EF4444' : '#6B7280' }}>{isOrderPdf(f) ? 'PDF' : 'FILE'}</span>
                          )}
                        </button>
                        <div style={{ padding:'6px 8px' }}>
                          <div title={orderFileName(f)} style={{ fontSize:11,fontWeight:600,color:'#374151',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{orderFileName(f)}</div>
                          <div style={{ display:'flex',gap:8,marginTop:4 }}>
                            <button type="button" onClick={() => setOrderPreviewFile({ name: orderFileName(f), url: orderFileUrl(f), source: 'uploaded' })} style={{ border:'none',background:'transparent',color:'#976793',fontSize:10,fontWeight:700,cursor:'pointer',padding:0 }}>Preview</button>
                            <a href={orderFileUrl(f)} target="_blank" rel="noreferrer" style={{ color:'#6B7280',fontSize:10,textDecoration:'none' }}>Open</a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sub-tabs */}
              <div style={{ display:'flex',borderBottom:'1px solid #E5E7EB',padding:'0 14px',background:'#fff' }}>
                {[['section_summary','Section Summary'],['items','Items']].map(([tab,label])=>(
                  <button key={tab} onClick={()=>setSubTab(order._id,tab)}
                    style={{ padding:'7px 12px',fontSize:12,fontWeight:getSubTab(order._id)===tab?600:400,color:getSubTab(order._id)===tab?'#976793':'#6B7280',background:'transparent',border:'none',borderBottom:getSubTab(order._id)===tab?'2px solid #976793':'2px solid transparent',cursor:'pointer',marginBottom:-1 }}>
                    {label}
                  </button>
                ))}
              </div>

              {getSubTab(order._id)==='section_summary' && order.orderType === 'Work Order' && (
                <div style={{ padding:'12px 14px' }}>
                  <table style={{ width:'100%',borderCollapse:'collapse' }}>
                    <thead>
                      <tr>
                        {['Description','Charge (₹)'].map((h) => (
                          <th key={h} style={h === 'Charge (₹)' ? { ...thS, textAlign:'right' } : thS}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleWorkOrderCharges(order).length === 0 ? (
                        <tr><td colSpan={2} style={{ textAlign:'center',padding:'1.5rem',color:'#9CA3AF',fontSize:12 }}>No work order lines</td></tr>
                      ) : (
                        visibleWorkOrderCharges(order).map((c, i) => (
                          <tr key={`${c.chargeName || c.labourType || 'line'}-${i}`} style={{ background:'#FFFBEB', boxShadow:'inset 3px 0 0 #F59E0B' }}>
                            <td style={tdS}>{c.chargeName || c.labourType || '—'}</td>
                            <td style={{ ...tdS, textAlign:'right', fontWeight:600 }}>₹ {formatInr(Number(c.value) || 0)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  <div style={{ textAlign:'right',padding:'10px 0',fontSize:12,color:'#6B7280' }}>
                    <div>Lines: {visibleWorkOrderCharges(order).length}</div>
                    <div style={{ fontWeight:700,color:'#1F2937' }}>Total: ₹{formatInr(order.total || 0)}</div>
                  </div>
                </div>
              )}

              {getSubTab(order._id)==='section_summary' && order.orderType !== 'Work Order' && (
                <div style={{ padding:'12px 14px' }}>
                  <table style={{ width:'100%',borderCollapse:'collapse' }}>
                    <thead>
                      <tr>
                      {['Section','Items','Base Total','Discount','Sub Total'].map(h=><th key={h} style={thS}>{h}</th>)}
                      </tr>
                      <tr style={{ background:'#FAFAFA' }}>
                        <th style={{ ...thS, paddingTop:4, paddingBottom:8, verticalAlign:'top' }}>
                          {(() => {
                            const lf = orderLineFiltersById[order._id] || emptyOrderLineFilters();
                            return <ColumnFilterInput value={lf.sectionLabel} onChange={(e) => patchOrderLineFilters(order._id, { sectionLabel: e.target.value })} placeholder="Filter section…" aria-label="Filter section row" />;
                          })()}
                        </th>
                        <th style={{ ...thS, paddingTop:4 }} colSpan={4} />
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const lf = orderLineFiltersById[order._id] || emptyOrderLineFilters();
                        const all = order.items || [];
                        const scoped = all.filter((it) => matchesOrderLine(it, lf));
                        const secMap = buildSections(scoped);
                        const sl = normalizeSearch(lf.sectionLabel);
                        const entries = Object.entries(secMap).filter(([sec]) => !sl || String(sec).toLowerCase().includes(sl));
                        if (all.length === 0) {
                          return <tr><td colSpan={5} style={{ textAlign:'center',padding:'1.5rem',color:'#9CA3AF',fontSize:12 }}>No items</td></tr>;
                        }
                        if (scoped.length > 0 && entries.length === 0) {
                          return <tr><td colSpan={5} style={{ textAlign:'center',padding:'1.5rem',color:'#9CA3AF',fontSize:12 }}>No sections match filters</td></tr>;
                        }
                        if (scoped.length === 0) {
                          return <tr><td colSpan={5} style={{ textAlign:'center',padding:'1.5rem',color:'#9CA3AF',fontSize:12 }}>No lines match filters</td></tr>;
                        }
                        return entries.map(([sec,d])=>(
                        <tr key={sec}><td style={tdS}>{sec}</td><td style={tdS}>{d.items}</td><td style={tdS}>₹ {formatInr(d.base)}</td><td style={tdS}>₹ {formatInr(0)}</td><td style={{...tdS,textAlign:'right'}}>₹ {formatInr(d.base)}</td></tr>
                      ));
                      })()}
                    </tbody>
                  </table>
                  <div style={{ textAlign:'right',padding:'10px 0',fontSize:12,color:'#6B7280' }}>
                    <div>Sub Total : ₹{formatInr(order.subTotal || 0)}</div>
                    {(order.gstTotal || 0) > 0 ? <div>GST: ₹{formatInr(order.gstTotal || 0)}</div> : null}
                    <div style={{ fontWeight:700,color:'#1F2937' }}>Total: ₹{formatInr(order.total || 0)}</div>
                  </div>
                </div>
              )}

              {getSubTab(order._id)==='items' && order.orderType === 'Work Order' && (
                <div style={{ padding:'12px 14px',overflowX:'auto' }}>
                  <table style={{ width:'100%',borderCollapse:'collapse',minWidth:420 }}>
                    <thead>
                      <tr>
                        {['Sr.No.','Description','Charge (₹)'].map((h) => (
                          <th key={h} style={h === 'Charge (₹)' ? { ...thS, textAlign:'right' } : thS}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleWorkOrderCharges(order).length === 0 ? (
                        <tr><td colSpan={3} style={{ textAlign:'center',padding:'1.5rem',color:'#9CA3AF',fontSize:12 }}>No lines</td></tr>
                      ) : (
                        visibleWorkOrderCharges(order).map((c, i) => (
                          <tr key={`${c.chargeName}-${i}`} style={{ background:'#FFFBEB', boxShadow:'inset 3px 0 0 #F59E0B' }}>
                            <td style={tdS}>{i + 1}</td>
                            <td style={tdS}>{c.chargeName || c.labourType || '—'}</td>
                            <td style={{ ...tdS, textAlign:'right', fontWeight:600 }}>₹ {formatInr(Number(c.value) || 0)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {getSubTab(order._id)==='items' && order.orderType !== 'Work Order' && (
                <div style={{ padding:'12px 14px',overflowX:'auto' }}>
                  <table style={{ width:'100%',borderCollapse:'collapse',minWidth:600 }}>
                    <thead>
                      <tr>
                      {['Sr.No.','Item','Section','UOM','Qty','Rate','Amount'].map((h) => (
                        <th key={h} style={h === 'Amount' ? { ...thS, textAlign:'right' } : thS}>{h}</th>
                      ))}
                      </tr>
                      <tr style={{ background:'#FAFAFA' }}>
                        <th style={{ ...thS, paddingTop:4, paddingBottom:8 }} />
                        {(() => {
                          const lf = orderLineFiltersById[order._id] || emptyOrderLineFilters();
                          return (
                            <>
                              <th style={{ ...thS, paddingTop:4, paddingBottom:8, verticalAlign:'top' }}>
                                <ColumnFilterInput value={lf.item} onChange={(e) => patchOrderLineFilters(order._id, { item: e.target.value })} placeholder="Filter item…" aria-label="Filter by item" />
                              </th>
                              <th style={{ ...thS, paddingTop:4, paddingBottom:8, verticalAlign:'top' }}>
                                <ColumnFilterInput value={lf.section} onChange={(e) => patchOrderLineFilters(order._id, { section: e.target.value })} placeholder="Filter section…" aria-label="Filter by section" />
                              </th>
                              <th style={{ ...thS, paddingTop:4, paddingBottom:8 }} />
                              <th style={{ ...thS, paddingTop:4, paddingBottom:8 }} />
                              <th style={{ ...thS, paddingTop:4, paddingBottom:8 }} />
                              <th style={{ ...thS, paddingTop:4, paddingBottom:8 }} />
                            </>
                          );
                        })()}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const lf = orderLineFiltersById[order._id] || emptyOrderLineFilters();
                        const all = order.items || [];
                        const rows = all.filter((it) => matchesOrderLine(it, lf));
                        if (all.length === 0) {
                          return <tr><td colSpan={7} style={{ textAlign:'center',padding:'1.5rem',color:'#9CA3AF',fontSize:12 }}>No items</td></tr>;
                        }
                        if (rows.length === 0) {
                          return <tr><td colSpan={7} style={{ textAlign:'center',padding:'1.5rem',color:'#9CA3AF',fontSize:12 }}>No lines match filters</td></tr>;
                        }
                        return rows.map((it,i)=>{
                        const q = Number(it.qty) || 0;
                        const r = Number(it.rate) || 0;
                        const amt = (it.amount != null && it.amount !== '') ? Number(it.amount) : q * r;
                        return (
                          <tr key={it._id||i} style={{ background:'#FFFBEB', boxShadow:'inset 3px 0 0 #F59E0B' }}>
                            <td style={tdS}>{i+1}</td>
                            <td style={tdS}>
                              <div style={{ fontWeight:600,fontSize:12,color:'#1F2937' }}>{orderLineName(it)}</div>
                              {orderLineDescription(it) ? (
                                <div style={{ fontSize:11,color:'#6B7280',marginTop:4,lineHeight:1.35 }}>{orderLineDescription(it)}</div>
                              ) : null}
                            </td>
                            <td style={tdS}>{it.section || '—'}</td>
                            <td style={tdS}>{it.uom || '—'}</td>
                            <td style={tdS}>{it.qty ?? 1}</td>
                            <td style={tdS}>₹ {formatInr(it.rate || 0)}</td>
                            <td style={{ ...tdS, fontWeight:600, textAlign:'right' }}>₹ {formatInr(amt)}</td>
                          </tr>
                        );
                      });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {orders.length > 0 && (
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap',padding:'12px 0 6px',fontSize:12,color:'#6B7280' }}>
          <span>Showing all {orders.length} order{orders.length === 1 ? '' : 's'}</span>
        </div>
      )}

      {orderPreviewFile && (
        <div
          onClick={() => setOrderPreviewFile(null)}
          style={{ position:'fixed',inset:0,zIndex:2000,background:'rgba(17,24,39,0.82)',display:'flex',alignItems:'center',justifyContent:'center',padding:24 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width:'min(920px, 94vw)',maxHeight:'90vh',background:'#fff',borderRadius:12,overflow:'hidden',boxShadow:'0 24px 70px rgba(0,0,0,0.28)' }}
          >
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,padding:'12px 16px',borderBottom:'1px solid #E5E7EB' }}>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:14,fontWeight:700,color:'#1F2937',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                  {orderFileName(orderPreviewFile)}
                </div>
                <div style={{ fontSize:11,color:'#9CA3AF' }}>
                  {isOrderImage(orderPreviewFile) ? 'Image preview' : isOrderPdf(orderPreviewFile) ? 'PDF preview' : 'File preview'}
                </div>
              </div>
              <div style={{ display:'flex',gap:8 }}>
                {orderFileUrl(orderPreviewFile) ? (
                  <a href={orderFileUrl(orderPreviewFile)} target="_blank" rel="noreferrer" style={{ padding:'7px 12px',border:'1px solid #E5E7EB',borderRadius:8,color:'#374151',textDecoration:'none',fontSize:12,fontWeight:600 }}>
                    Open
                  </a>
                ) : null}
                <button type="button" onClick={() => setOrderPreviewFile(null)} style={{ width:32,border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',color:'#6B7280',fontSize:16,cursor:'pointer' }}>
                  x
                </button>
              </div>
            </div>
            <div style={{ height:'min(72vh, 680px)',background:'#F9FAFB',display:'flex',alignItems:'center',justifyContent:'center' }}>
              {isOrderImage(orderPreviewFile) ? (
                <img src={orderFileUrl(orderPreviewFile)} alt={orderFileName(orderPreviewFile)} style={{ maxWidth:'100%',maxHeight:'100%',objectFit:'contain' }} />
              ) : isOrderPdf(orderPreviewFile) ? (
                <iframe title={orderFileName(orderPreviewFile)} src={orderFileUrl(orderPreviewFile)} style={{ width:'100%',height:'100%',border:0 }} />
              ) : (
                <div style={{ textAlign:'center',color:'#6B7280',padding:24 }}>
                  <div style={{ fontSize:42,fontWeight:800,marginBottom:10 }}>FILE</div>
                  <div style={{ fontSize:13,marginBottom:12 }}>Preview is not available for this file type.</div>
                  {orderFileUrl(orderPreviewFile) ? (
                    <a href={orderFileUrl(orderPreviewFile)} target="_blank" rel="noreferrer" style={{ color:'#976793',fontWeight:700 }}>
                      Open file
                    </a>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
